import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, ArrowRight, Shield, Loader2 } from "lucide-react";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { toast } from "sonner";
import { formatTime } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";

const OtpVerificationPage = () => {
  const { state } = useLocation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // const timerRef = useRef<NodeJS.Timeout | null>(null);

  const otpSession = JSON.parse(
    sessionStorage.getItem("otpVerification") || "{}",
  );

  const email = state?.payload?.email ?? otpSession?.email ?? null;

  useEffect(() => {
    if (!email) {
      navigate("/login", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const initializeTimer = () => {
    const saved = sessionStorage.getItem("otpVerification");
    if (!saved) return;

    const data = JSON.parse(saved);

    const expiry = new Date(data.issuedAt).getTime() + data.cooldown * 1000;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const update = () => {
      const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));

      setTimer(remaining);

      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        sessionStorage.removeItem("otpVerification");
      }
    };

    update();

    timerRef.current = setInterval(update, 1000);
  };

  useEffect(() => {
    initializeTimer();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];

      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }

      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join("");

    const data = {
      otp: otpString,
      email,
    };

    setIsVerifying(true);
    try {
      const res = await makeRequest.put("/auth/email/verify", data);

      console.log(res);

      if (res.data) {
        toast.success(res.data.message);
        navigate("/login");
        sessionStorage.removeItem("otpVerification");
      }
      setIsVerifying(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.log(error);
      setIsVerifying(false);
    }
  };

  const handleResend = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    setOtp(["", "", "", "", "", ""]);

    setIsLoading(true);

    try {
      const res = await makeRequest.put("/auth/email/verify/otp/resend", {
        email,
      });

      const data = res.data;

      sessionStorage.setItem(
        "otpVerification",
        JSON.stringify({
          email,
          issuedAt: data.otp.issuedAt,
          cooldown: data.otp.resendCooldownSeconds,
        }),
      );

      initializeTimer();

      toast.success(data.message);
      setIsLoading(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <div className="w-full sm:max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Enter OTP</h1>
          <p className="text-muted-foreground">
            Enter the 6-digit code sent to {email}
          </p>
        </div>

        <div className="nex-card p-8">
          <div className="space-y-6">
            <div className="flex justify-center gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-14 text-center text-xl font-bold bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                />
              ))}
            </div>

            <Button
              onClick={handleVerify}
              type="submit"
              className="w-full h-11 disabled:cursor-not-allowed"
              disabled={otp.some((d) => !d) || isVerifying || isLoading}
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <div className="text-center">
              {timer > 0 ? (
                <p className="text-muted-foreground">
                  Resend code in {formatTime(timer)}
                </p>
              ) : (
                <button
                  disabled={isLoading}
                  onClick={handleResend}
                  className="text-primary hover:underline disabled:cursor-not-allowed"
                >
                  {isLoading ? <Spinner /> : "Resend OTP"}
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-center text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpVerificationPage;
